import React from 'react';
import moment from 'moment';
import OpenPanel from '../../../../Component/Analytics/Components/OpenActivity/OpenActivityTable/OpenPanel/OpenPanel';
import ClickPanel from '../../../../Component/Analytics/Components/ClickActivity/ClickActivityTable/ClickPanel/ClickPanel';
import { capitalizeFirstLetter } from '../../../../Component/Analytics/utils';

const formatTime = time =>
  time ? moment.unix(time).format('MM-DD-YYYY hh:mm A') : 'N/A';

// The Message column: the draft name, and the version of an A/B message, as
// the old drawers title it ("(Version B)").
export const getMessageLabel = row =>
  row.version ? `${row.draftName} (Version ${row.version})` : row.draftName;

const MESSAGE_COLUMN = {
  title: 'Message',
  field: 'draftName',
  render: getMessageLabel,
};

// Event column of the old report drawers (FailedDeliveryReport).
const getEventLabel = row => {
  if (!row.bounceType) return capitalizeFirstLetter(row.eventName);
  if (row.bounceType === 'bounce') return 'Permanent Bounce';
  return 'Temporary Bounce';
};

const contactCsvColumns = row => ({
  'Full Name': row.fullName,
  'M1 ID': row.m1_id,
  'Member ID': row.memberId,
  Email: row.email,
  Phone: row.phone,
});

const reportConfig = (title, type, emptyText) => channel => ({
  title,
  kind: 'errors',
  params: { type, channel },
  emptyText,
  columns: [
    MESSAGE_COLUMN,
    { title: 'Contact', field: 'contact' },
    { title: 'Date', field: 'time', render: row => formatTime(row.time) },
    { title: 'Event', field: 'eventName', render: getEventLabel },
  ],
  detailPanel: row => (
    <div style={{ padding: '12px 24px' }}>
      <b>Error Reason: </b>
      {`${row.errorCode} ${row.errorMessage}`}
    </div>
  ),
  getCsvRows: rows =>
    rows.map(row => ({
      Message: getMessageLabel(row),
      ...contactCsvColumns(row),
      Event: getEventLabel(row),
      'Date/Time': formatTime(row.time),
      Code: row.errorCode,
      Reason: row.errorMessage,
    })),
});

// Per tile, for a channel ('all', 'email' or 'text'): the drawer title, the
// andromeda route (kind) and its parameters, the table columns, the detail
// panel under a row, and the CSV rows for the drawer's Export.
export const DRAWER_CONFIGS = {
  delivery: channel => ({
    title: 'Delivery Activity',
    kind: 'deliveries',
    params: { channel },
    emptyText: 'No delivery events found for these messages.',
    columns: [
      MESSAGE_COLUMN,
      ...(channel === 'all' ? [{ title: 'Channel', field: 'channel' }] : []),
      { title: 'Contact', field: 'contact' },
      {
        title: 'Delivery Date',
        field: 'deliveryTime',
        render: row => formatTime(row.deliveryTime),
      },
    ],
    getCsvRows: rows =>
      rows.map(row => ({
        Message: getMessageLabel(row),
        Channel: row.channel,
        ...contactCsvColumns(row),
        'Date/Time': formatTime(row.deliveryTime),
        'Delivered Yes/No': row.wasDelivered ? 'Yes' : 'No',
      })),
  }),
  open: () => ({
    title: 'Open Activity',
    kind: 'opens',
    params: {},
    emptyText: 'No open events found for these messages.',
    columns: [
      MESSAGE_COLUMN,
      { title: 'Contact', field: 'email' },
      { title: 'Total Opens', field: 'totalOpens' },
      {
        title: 'Last Opened Date',
        field: 'latestOpenTime',
        render: row => formatTime(row.latestOpenTime),
      },
    ],
    detailPanel: row => (
      <OpenPanel contactOpenActivity={row.openTimes.map(time => ({ time }))} />
    ),
    // One CSV row per open, as the old Open Activity export.
    getCsvRows: rows =>
      rows.flatMap(row =>
        row.openTimes.map(time => ({
          Message: getMessageLabel(row),
          ...contactCsvColumns(row),
          'Open Date/Time': formatTime(time),
        }))
      ),
  }),
  clickThrough: () => ({
    title: 'Click Activity',
    kind: 'clicks',
    params: {},
    emptyText: 'No click events found for these messages.',
    columns: [
      MESSAGE_COLUMN,
      { title: '#', field: 'displayOrder' },
      { title: 'Link URL', field: 'link' },
      { title: 'Total Clicks', field: 'totalClicks' },
      { title: 'Unique Clicks', field: 'uniqueClicks' },
    ],
    // The old drawer's panel lists the link's contacts for one message. It
    // takes the link's position counted from 1.
    detailPanel: row => (
      <ClickPanel messageId={row.messageId} linkIndex={row.linkIndex + 1} />
    ),
    getCsvRows: rows =>
      rows.map(row => ({
        Message: getMessageLabel(row),
        '#': row.displayOrder,
        'Link URL': row.link,
        'Total Clicks': row.totalClicks,
        'Unique Clicks': row.uniqueClicks,
      })),
  }),
  bounce: reportConfig(
    'Bounce Report',
    'bounce',
    'No recorded bounces for these messages.'
  ),
  drop: reportConfig(
    'Dropped Emails Report',
    'dropped',
    'No recorded drops for these messages.'
  ),
  spam: reportConfig(
    'Spam Reports',
    'spamreport',
    'No recorded spam reports for these messages.'
  ),
};
